import { CapacitorHttp, HttpHeaders } from '@capacitor/core'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'

import { requestReauth } from '@/utils/authReauthBridge'
import { authUtils } from '@/utils/auth'
import {
  LEGAL_ACCEPTANCE_ERROR_CODE,
  isLegalAcceptanceError,
  pendingLegalDocuments,
} from '@/utils/legal'
import { Logger } from '@/lib/logger'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface RequestOptions<D = undefined, P = undefined> {
  method: HttpMethod
  url: string
  params?: P
  data?: D
  headers?: Record<string, string>
  allowForbiddenError?: boolean
}

export interface RequestConfig {
  allowForbiddenError?: boolean
}

export type APIError =
  | {
      type: string
      errors: {
        code: string
        detail: string
        attr: string | null
      }[]
    }
  | { message: string }

export interface Response<R> {
  data: R
  status: number
  headers: HttpHeaders
  url: string
}

export class HttpError extends Error {
  public status: number
  public code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)

    this.status = status
    this.code = code
  }
}

const TOKEN_KEY = 'auth_token'

class HttpService {
  private baseUrl: string
  private logger = new Logger('HttpService')

  constructor() {
    const base = process.env.REACT_APP_API_URL

    if (!base) {
      throw new HttpError(-1, 'REACT_APP_API_URL não está definido nas variáveis de ambiente.')
    }

    this.logger.info(`API base URL: ${base}`)

    this.baseUrl = base
  }

  private removeUndefined(obj: unknown): unknown {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.removeUndefined(item))
    } else if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj)
        .filter(([, value]) => value !== undefined)
        .reduce(
          (acc, [key, value]) => {
            acc[key] = this.removeUndefined(value)
            return acc
          },
          {} as Record<string, unknown>
        )
    }

    return obj
  }

  private serializeParams(params: unknown): Record<string, string> | undefined {
    if (typeof params !== 'object' || params === null) {
      return undefined
    }

    const serialized: Record<string, string> = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        serialized[key] = String(value)
      }
    })
    return serialized
  }

  private handleErrors(response: Response<APIError>): void {
    const { status, data } = response
    const code = 'errors' in data && data.errors.length > 0 ? data.errors[0].code : undefined

    if (status >= 400 && status < 500) {
      throw new HttpError(
        status,
        'errors' in data && data.errors.length > 0
          ? data.errors[0].detail
          : 'message' in data
            ? data.message
            : 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.',
        code
      )
    } else if (status >= 500) {
      throw new HttpError(
        status,
        'errors' in data && data.errors.length > 0
          ? data.errors[0].detail
          : 'message' in data
            ? data.message
            : 'Ocorreu um erro interno no servidor. Por favor, tente novamente mais tarde.',
        code
      )
    }
  }

  public async request<R = unknown, D = undefined, P = undefined>(
    options: RequestOptions<D, P>,
    _retried = false
  ): Promise<Response<R>> {
    let token: string | null
    try {
      token = (await SecureStorage.get(TOKEN_KEY)) as string | null
    } catch {
      token = null
    }

    try {
      const cleanedParams = options.params ? this.serializeParams(this.removeUndefined(options.params)) : undefined
      const cleanedData = options.data ? this.removeUndefined(options.data) : undefined

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(options.headers || {}),
      }

      if (token && authUtils.isTokenValid(token)) {
        headers['Authorization'] = `Bearer ${token}`
      }

      this.logger.info(`${options.method} ${options.url}`, { params: cleanedParams, data: cleanedData })

      const response = await CapacitorHttp.request({
        method: options.method,
        url: `${this.baseUrl}${options.url}`,
        params: cleanedParams,
        data: cleanedData,
        headers,
      })

      const isAuthRoute = options.url.startsWith('auth') || options.url.startsWith('/auth')

      if (response.status === 401 && isLegalAcceptanceError(response.data)) {
        const documents = pendingLegalDocuments(response.data)

        this.logger.warn(`Legal acceptance required on ${options.url}`)
        window.dispatchEvent(
          new CustomEvent('legal:acceptance-required', { detail: { documents } })
        )
        throw new HttpError(
          response.status,
          'É necessário aceitar a versão vigente dos documentos legais.',
          LEGAL_ACCEPTANCE_ERROR_CODE
        )
      }

      if (response.status === 401 && !isAuthRoute) {
        if (!_retried) {
          this.logger.warn(`401 on ${options.url}, attempting reauth`)
          const reauthOk = await requestReauth()
          if (reauthOk) {
            return this.request<R, D, P>(options, true)
          }
        }
        this.logger.error(`Session expired on ${options.url}`)
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
        throw new HttpError(response.status, 'Sessão expirada. Faça login novamente.')
      }

      if (response.status === 403 && !isAuthRoute && !options.allowForbiddenError) {
        this.logger.warn(`403 Forbidden on ${options.url}`)
        window.dispatchEvent(new CustomEvent('auth:redirect', { detail: { url: '/?error=forbidden' } }))
        return response as Response<R>
      }

      this.handleErrors(response)

      this.logger.success(`${options.method} ${options.url} → ${response.status}`)

      return response as Response<R>
    } catch (error: unknown) {
      if (!(error instanceof HttpError)) {
        this.logger.error(`Unexpected error on ${options.method} ${options.url}`, error)
        throw new HttpError(-1, 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.')
      }

      this.logger.error(`${options.method} ${options.url} → ${error.status}`, error.message)

      throw error
    }
  }

  public get<R = unknown, P = undefined>(url: string, params?: P, config?: RequestConfig): Promise<Response<R>> {
    return this.request<R, undefined, P>({
      method: 'GET',
      url,
      params,
      ...config,
    })
  }

  public post<R = unknown, D = unknown>(url: string, data?: D): Promise<Response<R>> {
    return this.request<R, D, undefined>({
      method: 'POST',
      url,
      data,
    })
  }

  public put<R = unknown, D = unknown>(url: string, data?: D): Promise<Response<R>> {
    return this.request<R, D, undefined>({
      method: 'PUT',
      url,
      data,
    })
  }

  public patch<R = unknown, D = unknown, P = undefined>(url: string, data?: D, params?: P): Promise<Response<R>> {
    return this.request<R, D, P>({
      method: 'PATCH',
      url,
      data,
      params,
    })
  }

  public delete<R = unknown, P = undefined>(url: string, params?: P): Promise<Response<R>> {
    return this.request<R, undefined, P>({
      method: 'DELETE',
      url,
      params,
    })
  }
}

const http = new HttpService()

export default http
